import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { RunStatus } from '../../../../backend/src/types/types';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { isProcessingStatus } from '@/new/lib/pipeline-status';

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  currentSubsetId: string;
  docIds: string[];
  docDescription?: string;
}

export function AddToSubsetDialog({ isOpen, onOpenChange, matterId, currentSubsetId, docIds, docDescription }: Props) {
  const navigate = useNavigate();
  const { data: allSubsets = [], isLoading: isSubsetsLoading } = useGetSubsets(matterId);

  const subsets = useMemo(
    () => allSubsets.filter((s) => !s.isAllDocuments && s.id !== currentSubsetId && s.status !== 'processing'),
    [allSubsets, currentSubsetId],
  );

  const handleAddToSubset = (targetSubsetId: string) => {
    onOpenChange(false);
    navigate(`/matters/${matterId}/subsets/${targetSubsetId}/edit`, {
      state: { additionalDocIds: docIds },
    });
  };

  const docCount = docIds.length;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--group" />
            Add to Existing Subset
          </DialogTitle>
          <DialogDescription>
            Add {docCount} {docDescription ?? (docCount === 1 ? 'document' : 'documents')} to an existing subset.
            Documents already in the subset will be skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-start">
          <div className="w-full">
            {isSubsetsLoading ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Loading Subsets...
              </div>
            ) : subsets.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">
                No existing subsets found. Use &quot;Create New&quot; to create one.
              </p>
            ) : (
              <div className="flex w-full flex-col justify-between gap-2">
                {subsets.map((subset) => {
                  const hasActiveRun = subset.runs?.some(
                    (run) => run.status && isProcessingStatus(run.status as RunStatus),
                  );
                  return (
                    <div
                      key={subset.id}
                      className="flex items-center justify-between rounded-md border bg-gradient-to-r from-gray-50 px-4 py-2.5 pr-2.5 text-sm"
                    >
                      <div className="flex flex-col">
                        <p className="line-clamp-1 font-medium text-ellipsis">{subset.name}</p>
                        <p className="text-xs text-gray-500">
                          Created {formatDate(subset.createdDate)}
                          {subset.docCount != null && ` · ${subset.docCount} documents`}
                        </p>
                      </div>
                      {hasActiveRun ? (
                        <p className="flex items-center gap-1.5 pr-2 text-xs text-sky-500">
                          <Icon className="lucide--info mb-px" />
                          Waiting for processing to finish
                        </p>
                      ) : (
                        <Button className="bg-gray-100" onClick={() => handleAddToSubset(subset.id)}>
                          <Icon className="lucide--plus" /> Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
